import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--secondary)" }}>
      <div className="bg-white rounded-2xl border p-10 w-full max-w-md" style={{ borderColor: "var(--border)" }}>

        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-sm opacity-60 hover:opacity-100 mb-8"
        >
          <ArrowLeft size={16} /> Back to login
        </button>

        {sent ? (
          <div className="text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: "#dcfce7" }}
            >
              <Mail size={28} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Fraunces, serif" }}>
              Check your inbox
            </h1>
            <p className="text-sm opacity-70 mb-2 leading-relaxed">
              If an account exists for <strong>{email}</strong>, we've sent a password
              reset link. It expires in 1 hour.
            </p>
            <p className="text-sm opacity-50 mb-8">
              Didn't receive it? Check your spam folder or try again.
            </p>
            <Button
              variant="outline"
              className="w-full mb-3"
              onClick={() => { setSent(false); setEmail(""); }}
            >
              Try a different email
            </Button>
            <button
              className="text-sm opacity-60 underline"
              onClick={() => navigate("/login")}
            >
              Back to login
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Fraunces, serif" }}>
              Forgot your password?
            </h1>
            <p className="text-sm opacity-60 mb-8 leading-relaxed">
              Enter the email address associated with your MoolTax account and we'll
              send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full btn-primary"
                data-testid="forgot-submit-btn"
              >
                {submitting ? "Sending..." : "Send reset link"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;